import { Container, Grid, Typography, Link, Divider, Avatar, Box, Skeleton, LinearProgress } from "@mui/material";
import { LoadingButton } from '@mui/lab';
import { Component } from 'react';

const he = require('he');
const dividerPost = {
    borderBottomWidth: '1px', 
    mt: 1,
};

class PostFeed extends Component {

    constructor(props) {
        super(props);
        this.state = {
            pageToken: '',
            feed: new Map(),
            loading: false,
            showInitialLoader: false,
        };
    
        // This binding is necessary to make `this` work in the callback
        this.fetchPosts = this.fetchPosts.bind(this);
      }
    
    componentDidMount() {
        // strict mode will cause componentDidMount() called twice
        // see https://stackoverflow.com/questions/71755119/reactjs-componentdidmount-executes-twice
        if (this.mounted) {
            return
        }
        this.mounted = true
        this.fetchPosts()    
    }

    componentWillUnmount() {
        if (this.loadingDelayTimer) {
            clearTimeout(this.loadingDelayTimer)
        }
    }

    fetchPosts() {
        if (this.loadingDelayTimer) {
            clearTimeout(this.loadingDelayTimer)
        }

        const shouldDelayInitialLoader = this.state.feed.size === 0
        if (shouldDelayInitialLoader) {
            this.loadingDelayTimer = setTimeout(() => {
                this.setState({ showInitialLoader: true })
            }, 180)
        }

        this.setState(
            { loading: true, showInitialLoader: false },
            () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/posts?limit=20&page_token=${this.state.pageToken}`)
                .then((resp) => resp.json())
                .then((resp) => {
                    if (this.loadingDelayTimer) {
                        clearTimeout(this.loadingDelayTimer)
                        this.loadingDelayTimer = null
                    }
                    this.setState((state, props) => {
                        let feed = new Map(this.state.feed)
                        resp.posts.forEach(p => {
                            const date = new Date(p.published_time).toLocaleDateString()
                            if (!feed.has(date)) {
                            feed.set(date, [])
                            }
                            // feed.get(date).push(p)
                            feed.set(date, feed.get(date).concat([p]))
                        })
                        return {
                            pageToken: resp.page_token, 
                            feed: feed,
                            loading: false,
                            showInitialLoader: false
                        }
                    })
                })
                .catch(() => {
                    if (this.loadingDelayTimer) {
                        clearTimeout(this.loadingDelayTimer)
                        this.loadingDelayTimer = null
                    }
                    this.setState({ loading: false, showInitialLoader: false })
                })
        )
    }
    
    render() {
        const initialLoading =
            this.state.loading &&
            this.state.feed.size === 0 &&
            this.state.showInitialLoader
        return (
            <Container 
                maxWidth="md"
                sx={{
                    marginTop: 2,
                    marginBottom: 2
                }}
            >
                {
                    this.state.loading &&
                    <Box
                        sx={{
                            mb: initialLoading ? 2 : 1,
                            borderRadius: 999,
                            overflow: 'hidden',
                            boxShadow: '0 0 0 1px rgba(91,207,128,0.12)'
                        }}
                    >
                        <LinearProgress
                            sx={{
                                height: 5,
                                backgroundColor: 'rgba(91,207,128,0.12)',
                                '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(90deg, #5BCF80 0%, #2FA7A1 100%)'
                                }
                            }}
                        />
                    </Box>
                }
                {initialLoading && <Box>{this.renderLoadingState()}</Box>}
                {
                    !initialLoading &&
                    <Grid container spacing={1}>
                        {this.renderDailyPosts()}
                    </Grid>
                }
                {
                    (this.state.pageToken !== '' && this.state.pageToken !== null) &&
                    <LoadingButton variant="text"
                        sx={{
                            float: 'right',
                            color: 'primary.dark',
                            ':hover': {
                                color: 'secondary.light'
                            }
                        }}
                        onClick={this.fetchPosts}
                        loading={this.state.loading}
                    >
                        <span>...More</span>
                    </LoadingButton>
                }  
            </Container>
        )
    }

    renderLoadingState() {
        return (
            <Box
                sx={{
                    mt: 2,
                    px: 2,
                    py: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(180deg, rgba(91,207,128,0.16) 0%, rgba(255,255,255,0.98) 55%, rgba(47,167,161,0.08) 100%)',
                    border: '1px solid rgba(91,207,128,0.22)',
                    boxShadow: '0 18px 40px rgba(47, 167, 161, 0.08)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            display: 'block',
                            fontFamily: 'Roboto Mono',
                            letterSpacing: '0.12em',
                            color: '#4B6354'
                        }}
                    >
                        Loading Latest Posts
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            fontFamily: 'Roboto Mono',
                            color: '#6A7A71'
                        }}
                    >
                        Fetching feed...
                    </Typography>
                </Box>
                {[0, 1, 2, 3].map((i) => (
                    <Grid
                        container
                        key={i}
                        sx={{
                            mb: 2.5,
                            opacity: 1 - (i * 0.12),
                            transform: `translateX(${i * 4}px)`
                        }}
                    >
                        <Avatar variant="square" sx={{ width: 24, height: 24, bgcolor: 'transparent' }}>
                            <Skeleton animation="wave" variant="rectangular" width={24} height={24} />
                        </Avatar>
                        <Grid item xs={8} sx={{ ml: 1 }}>
                            <Skeleton animation="wave" variant="text" width={110} height={22} />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 1.2 }}>
                            <Skeleton animation="wave" variant="text" width="92%" height={30} />
                            <Skeleton animation="wave" variant="text" width="76%" height={24} />
                            <Divider sx={dividerPost} />
                        </Grid>
                    </Grid>
                ))}
            </Box>
        )
    }

    renderDailyPosts() {
        return Array.from(this.state.feed.entries()).map((g) => {
            const date = g[0]
            const posts = g[1]
            
            return (
                <Grid 
                    container 
                    key={date}
                    spacing={1} 
                    sx={{mt: 3 }}    
                >
                    <Grid item xs={12}>
                        <Typography
                            marginBottom={2}
                            variant="h6"
                            align="center"
                            sx={{
                                display: 'inline',
                                fontFamily: 'Roboto Mono',
                                fontWeight: 'Bold',
                                color: '#3C3C3C',
                                background: 'linear-gradient(0deg, rgba(91,207,128, 0) 0%, rgba(91,207,128, 0) 30%, rgba(91,207,128, 0.3) 30%, rgba(91,207,128, 0.2) 55%, rgba(91,207,128, 0) 55%, rgba(91,207,128, 0) 100%)'
                            }}
                        >
                        {date}
                        </Typography>
                    </Grid>
                    {posts.map((p, i) => this.renderPost(p, i))}
                </Grid>                
            )
        })
    }

    renderPost(post, i) {
        return (
            <Grid container item key={i} xs={12} sx={{mt: 1, mx: 2}}>
                <Avatar
                    src={`${process.env.PUBLIC_URL}/site-icons/${post.icon_path}`}
                    variant='square'
                    alt={post.site}
                    sx={{ width: 24, height: 24 }}>
                </Avatar>
                <Grid item xs={8} md={8}
                    sx={{                    
                        mt: 0.3,
                        ml: 1
                    }}
                >
                    <Typography
                        variant='body2'
                        sx={{
                            fontFamily: 'Roboto Mono',
                            color: '#3C3C3C'
                        }}
                    >
                    {post.site}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={12}
                    sx={{mt: 1}}
                >
                    <Link
                        href={post.url}
                        target="_blank"
                        variant='subtitle1'
                        color='#3C3C3C'
                        sx={{
                            fontWeight: 'Bold',
                            textDecoration: 'none', 
                        }}                        
                    >
                    {he.unescape(post.title)}
                    </Link>
                    <Divider sx={dividerPost}/>
                </Grid>                
            </Grid>
        )
    }

}

export default PostFeed
